import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Share2, Copy, Download, Loader2, Check, School } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawCover(ctx, img, x, y, w, h) {
  const r = Math.max(w / img.width, h / img.height);
  const nw = img.width * r, nh = img.height * r;
  // anchor to top so faces are never cropped off
  ctx.drawImage(img, x + (w - nw) / 2, y, nw, nh);
}

/**
 * "Share my locked look" — generates a Snap/IG-ready 1080x1350 card
 * with the try-on/dress image, school + dress name and a share link.
 */
export function ShareLook({ open, onClose, imageUrl, productName, schoolName, productUrl, remainingLabel, productId }) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [blob, setBlob] = useState(null);
  const [copied, setCopied] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReady(false); setBlob(null);
    const render = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const W = 1080, H = 1350;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#140b1c"; ctx.fillRect(0, 0, W, H);
      let tainted = false;
      try {
        const img = await loadImage(imageUrl);
        drawCover(ctx, img, 0, 0, W, H);
      } catch { tainted = true; ctx.fillStyle = "#2a1a35"; ctx.fillRect(0, 0, W, H); }

      // gradient overlays
      const g = ctx.createLinearGradient(0, H * 0.45, 0, H);
      g.addColorStop(0, "rgba(10,7,15,0)"); g.addColorStop(1, "rgba(10,7,15,0.92)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      const gt = ctx.createLinearGradient(0, 0, 0, 240);
      gt.addColorStop(0, "rgba(10,7,15,0.7)"); gt.addColorStop(1, "rgba(10,7,15,0)");
      ctx.fillStyle = gt; ctx.fillRect(0, 0, W, 240);

      ctx.textAlign = "left";
      ctx.fillStyle = "#F7E7CE";
      ctx.font = "600 30px Manrope, sans-serif";
      ctx.fillText("HEATHER'S BOUTIQUE", 70, 90);
      ctx.fillStyle = "rgba(247,231,206,0.7)";
      ctx.font = "500 22px Manrope, sans-serif";
      ctx.fillText("WISCONSIN PROM NETWORK", 70, 128);

      // lock pill
      ctx.fillStyle = "#B76E79";
      ctx.beginPath(); ctx.roundRect(70, H - 470, 360, 64, 32); ctx.fill();
      ctx.fillStyle = "#FAF9F6"; ctx.font = "600 28px Manrope, sans-serif";
      ctx.fillText(`🔒 LOCKED · ${remainingLabel || "15:00"}`, 96, H - 428);

      ctx.fillStyle = "#FAF9F6";
      ctx.font = "500 74px 'Cormorant Garamond', Georgia, serif";
      ctx.fillText("I just locked the", 70, H - 320);
      ctx.fillStyle = "#F7E7CE";
      ctx.font = "600 92px 'Cormorant Garamond', Georgia, serif";
      const name = productName.length > 20 ? productName.slice(0, 19) + "…" : productName;
      ctx.fillText(name, 70, H - 230);
      ctx.fillStyle = "rgba(250,249,246,0.85)";
      ctx.font = "400 40px 'Cormorant Garamond', Georgia, serif";
      ctx.fillText(`for ${schoolName || "our"} Prom 2026 🌹`, 70, H - 165);
      ctx.fillStyle = "rgba(247,231,206,0.6)";
      ctx.font = "500 26px Manrope, sans-serif";
      ctx.fillText("Claim yours before someone else does →", 70, H - 100);

      setReady(true);
      if (!tainted) {
        canvas.toBlob((b) => setBlob(b), "image/png");
      }
    };
    render();
  }, [open, imageUrl, productName, schoolName, remainingLabel]);

  if (!open) return null;

  const shareLink = productUrl;
  const shareText = `I just locked the ${productName} for ${schoolName || "our"} Prom 2026 🔒🌹 Claim yours: ${shareLink}`;

  const doShare = async () => {
    try {
      if (blob && navigator.canShare && navigator.canShare({ files: [new File([blob], "look.png", { type: "image/png" })] })) {
        await navigator.share({ files: [new File([blob], "look.png", { type: "image/png" })], text: shareText, title: "My Heather's locked look" });
        return;
      }
      if (navigator.share) { await navigator.share({ text: shareText, url: shareLink }); return; }
      await navigator.clipboard.writeText(shareText);
      toast.success("Caption + link copied — paste it on your story!");
    } catch (e) { /* user cancelled */ }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareText);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
    toast.success("Copied!");
  };

  const download = () => {
    if (!blob) { toast.error("Image still rendering…"); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "my-locked-look.png"; a.click();
    URL.revokeObjectURL(url);
  };

  const postToSchool = async () => {
    if (!user?.school_verified) { toast.error("Verify your school email to post on your school page"); return; }
    setPosting(true);
    try {
      await api.createMemory({
        image_url: imageUrl,
        caption: `I just locked the ${productName} 🔒 One of one at ${schoolName || "my school"}.`,
        event: "prom", source: "locked_look", product_id: productId || null,
      });
      setPosted(true);
      toast.success("Featured on your school page ✨");
    } catch (e) { toast.error(errMsg(e)); }
    setPosting(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/75 backdrop-blur-md" />
      <div data-testid="share-look-modal" className="relative bg-ivory rounded-sm max-w-sm w-full overflow-hidden animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-ivory/80 hover:text-ivory"><X className="w-5 h-5" /></button>
        <div className="p-5 pb-3">
          <p className="font-label text-[9px] text-rosemetal mb-1">Share your locked look</p>
          <h3 className="font-display text-2xl">Make your school jealous 🔒</h3>
        </div>
        <div className="px-5">
          <div className="relative rounded-sm overflow-hidden bg-ink aspect-[4/5]">
            {!ready && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-champagne" /></div>}
            <canvas ref={canvasRef} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="p-5 grid grid-cols-3 gap-2">
          <button data-testid="share-native-btn" onClick={doShare} className="col-span-3 bg-ink text-ivory py-3.5 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4" /> Share to Story
          </button>
          {user && (
            posted ? (
              <Link data-testid="share-school-posted" to={user.school_id ? `/schools/${user.school_id}` : "/portal"} onClick={onClose} className="col-span-3 border border-rosemetal bg-blush/50 text-rosemetal py-3.5 font-label text-[10px] rounded-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> On your school page — see it
              </Link>
            ) : (
              <button data-testid="share-school-btn" onClick={postToSchool} disabled={posting} className="col-span-3 border border-ink py-3.5 font-label text-[10px] rounded-sm hover:bg-ink hover:text-ivory transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <School className="w-4 h-4" />} Feature on My School Page
              </button>
            )
          )}
          <button data-testid="share-copy-btn" onClick={copyLink} className="border border-border py-3 rounded-sm flex items-center justify-center gap-1.5 font-label text-[9px]">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy
          </button>
          <button data-testid="share-download-btn" onClick={download} className="border border-border py-3 rounded-sm flex items-center justify-center gap-1.5 font-label text-[9px]">
            <Download className="w-3.5 h-3.5" /> Save
          </button>
          <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" className="border border-border py-3 rounded-sm flex items-center justify-center font-label text-[9px]">WhatsApp</a>
        </div>
      </div>
    </div>
  );
}
